import React, { useEffect, useState, useRef } from 'react';
import { ArrowRightCircleIcon, ExternalLinkIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Norden } from './Norden';
import { motion } from 'framer-motion';
import { clientCases } from '../data/clientCases';
export const ClientCases: React.FC = () => {
  const cardsPerView = 3;
  const maxIndex = Math.max(0, clientCases.length - cardsPerView);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [cardWidth, setCardWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const gap = 32; // gap-8 = 32px
        const width = (containerWidth - gap * (cardsPerView - 1)) / cardsPerView;
        setCardWidth(width);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);
  const handlePrev = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };
  const handleNext = () => {
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  };
  return <div className="w-full bg-gradient-to-br from-[#F7F8FA] to-[#E8F0FE] py-16 md:py-[60px] relative overflow-hidden" data-id="element-112">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5" data-id="element-113">
        <div className="w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzAwN0FGRiIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')]" data-id="element-114"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-115">
        <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-116">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="font-bold text-[#111827] mb-4 text-3xl md:text-4xl" data-id="element-117">
            Klantcases
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-[#6B7280] mb-8 leading-relaxed text-lg" data-id="element-118">
            Bekijk hoe wij bedrijven helpen groeien met sterke websites en
            slimme online strategie. Deze cases laten zien hoe dienstverleners
            hun online zichtbaarheid hebben vergroot en meer aanvragen
            binnenhalen. Van betere vindbaarheid tot meer contactmomenten:
            ontdek wat er mogelijk is.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2,
          type: 'spring'
        }} className="relative inline-block" data-id="element-119">
            <Norden variant="sticker" className="opacity-80" data-id="element-120" />
          </motion.div>
        </div>

        {/* Carousel */}
        <div className="relative" data-id="element-121">
          {/* Navigation Buttons */}
          <button onClick={handlePrev} disabled={currentIndex === 0} className="absolute -left-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#007AFF] hover:bg-[#007AFF] hover:text-white transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#007AFF]" aria-label="Vorige cases" data-id="element-122">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-123" />
          </button>
          <button onClick={handleNext} disabled={currentIndex >= maxIndex} className="absolute -right-5 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#007AFF] hover:bg-[#007AFF] hover:text-white transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#007AFF]" aria-label="Volgende cases" data-id="element-124">
            <ChevronRightIcon className="w-6 h-6" data-id="element-125" />
          </button>

          {/* Cards Container */}
          <div ref={containerRef} className="overflow-hidden" data-id="element-126">
            <motion.div className="flex gap-8" animate={{
            x: cardWidth > 0 ? -(currentIndex * (cardWidth + 32)) : 0
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-127">
              {clientCases.map((clientCase, index) => <motion.div key={index} className="flex-shrink-0 bg-white rounded-2xl shadow-[0_8px_24px_rgba(0,122,255,0.08)] overflow-hidden transition-shadow duration-300 hover:shadow-[0_12px_32px_rgba(0,122,255,0.15)] group flex flex-col h-full" style={{
              width: cardWidth > 0 ? cardWidth : 'calc((100% - 64px) / 3)'
            }} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} data-id="element-128">
                  {/* Image */}
                  <div className="relative h-56 overflow-hidden" data-id="element-129">
                    <img src={clientCase.heroImage} alt={`${clientCase.clientName} - klantcase Noord Digital`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-130" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" data-id="element-131"></div>
                    <div className="absolute bottom-4 left-4 right-4" data-id="element-132">
                      <span className="inline-block bg-[#007AFF] text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-sm" data-id="element-133">
                        {clientCase.sector}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 flex flex-col flex-grow" data-id="element-134">
                    <h3 className="text-xl font-bold text-[#111827] mb-3" data-id="element-135">
                      {clientCase.clientName}
                    </h3>
                    <p className="text-[#6B7280] mb-6 leading-relaxed flex-grow" data-id="element-136">
                      {clientCase.shortDescription}
                    </p>
                    <a href={`/cases/${clientCase.slug}`} target="_self" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors group/link mt-auto w-fit" data-id="element-137">
                      Lees het verhaal
                      <ArrowRightCircleIcon className="h-5 w-5 ml-2 transition-transform group-hover/link:translate-x-1" data-id="element-138" />
                    </a>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-8" data-id="element-139">
            {Array.from({
            length: maxIndex + 1
          }).map((_, i) => <button key={i} onClick={() => setCurrentIndex(i)} className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${i === currentIndex ? 'bg-[#007AFF] w-8' : 'bg-[#007AFF]/20 hover:bg-[#007AFF]/40'}`} aria-label={`Ga naar slide ${i + 1}`} data-id="element-140" />)}
          </div>
        </div>
      </div>
    </div>;
};